// src/components/BrandAssets.js
// Brand Document and Brand Voice management for operators/admins

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import BrandIcon from './BrandIcon';
import Icon from './Icon';
import CommentModal from './CommentModal';
import '../styles/BrandAssets.css';

const BrandAssets = ({ user }) => {
  const modal = useModal();
  const canViewHistory = !!(user?.is_super_admin || user?.is_operator || user?.is_admin);
  const canDeleteActivity = !!(user?.is_super_admin || user?.is_admin);
  // Get selected brand from global context
  const { selectedBrand: contextBrand } = useOrgBrand();
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('brand_assets_active_tab') || 'document';
  });

  useEffect(() => {
    localStorage.setItem('brand_assets_active_tab', activeTab);
  }, [activeTab]);
  const [brandVoiceCompletion, setBrandVoiceCompletion] = useState(null);
  const [pendingBrandVoiceCompletions, setPendingBrandVoiceCompletions] = useState([]);
  const [brandVoiceHistory, setBrandVoiceHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [brandHubActivity, setBrandHubActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const ACTIVITY_FETCH_BATCH_SIZE = 100;
  const ACTIVITY_ITEMS_PER_PAGE = 10;
  const [commentModalState, setCommentModalState] = useState({ open: false, itemId: null, comments: [] });

  // Form state for brand document
  const [brandDocument, setBrandDocument] = useState({
    audience: '',
    mission_core_values: '',
    value_propositions: [],
    differentiators: [],
    products_services: '',
    locations: [],
    ideal_clients: '',
    industry_details: '',
    competitors: [],
    offers: []
  });

  // Form state for brand voice
  const [brandVoice, setBrandVoice] = useState({
    persona: '',
    traits: '',
    guidelines: '',
    excerpts: [],
    samples: []
  });

  // Form state for brand visuals
  const [brandVisuals, setBrandVisuals] = useState({
    menu_color: '#2E3336',
    primary_color: '#299DDB',
    secondary_color: '#20E27A',
    tertiary_color: '#FF5524',
    light_color: '#FFFFFF',
    dark_color: '#2B2D2F'
  });

  // Temporary input for array fields
  const [newItem, setNewItem] = useState({});

  // Load brand data when context brand changes
  useEffect(() => {
    if (contextBrand) {
      // Always load the brand data to ensure form fields are populated
      // This handles both brand changes and navigating back to the same brand
      selectBrand(contextBrand);
    } else {
      setSelectedBrand(null);
    }
  }, [contextBrand]);

  // Handle navigation from ToDoList for brand voice approval
  useEffect(() => {
    const openBrandVoiceTab = sessionStorage.getItem('open_brand_voice_tab');
    const pendingBrandVoiceId = sessionStorage.getItem('pending_brand_voice_id');

    if (openBrandVoiceTab === 'true' && pendingBrandVoiceId && contextBrand) {
      const contextId = contextBrand._id || contextBrand.id;

      if (contextId === pendingBrandVoiceId) {
        // Switch to voice tab
        setActiveTab('voice');
      }

      // Clear the flags
      sessionStorage.removeItem('open_brand_voice_tab');
      sessionStorage.removeItem('pending_brand_voice_id');
    }
  }, [contextBrand]);

  const selectBrand = async (brand) => {
    setSuccess(null);
    setError(null);

    // Fetch the full brand data including brand_document and brand_voice
    try {
      const brandId = brand._id || brand.id;
      const response = await axios.get(`/api/brands/${brandId}`, {
        withCredentials: true
      });
      const fullBrand = response.data;

      setSelectedBrand(fullBrand);

      // Load existing brand document and voice from the full brand data
      setBrandDocument({
        audience: fullBrand.brand_document?.audience || '',
        mission_core_values: fullBrand.brand_document?.mission_core_values || '',
        value_propositions: fullBrand.brand_document?.value_propositions || [],
        differentiators: fullBrand.brand_document?.differentiators || [],
        products_services: fullBrand.brand_document?.products_services || '',
        locations: fullBrand.brand_document?.locations || [],
        ideal_clients: fullBrand.brand_document?.ideal_clients || '',
        industry_details: fullBrand.brand_document?.industry_details || '',
        competitors: fullBrand.brand_document?.competitors || [],
        offers: fullBrand.brand_document?.offers || []
      });

      setBrandVoice({
        persona: fullBrand.brand_voice?.persona || '',
        traits: fullBrand.brand_voice?.traits || '',
        guidelines: fullBrand.brand_voice?.guidelines || '',
        excerpts: fullBrand.brand_voice?.excerpts || [],
        samples: fullBrand.brand_voice?.samples || []
      });

      setBrandVisuals({
        menu_color: fullBrand.menu_color || '#2E3336',
        primary_color: fullBrand.primary_color || '#299DDB',
        secondary_color: fullBrand.secondary_color || '#20E27A',
        tertiary_color: fullBrand.tertiary_color || '#FF5524',
        light_color: fullBrand.light_color || '#FFFFFF',
        dark_color: fullBrand.dark_color || '#2B2D2F'
      });

      // Fetch brand voice completion status for operators
      fetchBrandVoiceCompletion(fullBrand);
      fetchPendingBrandVoiceCompletions(fullBrand);
    } catch (error) {
      console.error('Error fetching full brand data:', error);
      setError('Failed to load brand data. Please try again.');
      setSelectedBrand(null);
    }
  };

  const fetchBrandVoiceCompletion = async (brand) => {
    const brandId = brand._id || brand.id;

    try {
      const response = await axios.get(`/api/brand-voice/completion/${brandId}`, {
        withCredentials: true
      });

      if (response.data.completion) {
        setBrandVoiceCompletion(response.data.completion);
      } else {
        setBrandVoiceCompletion(null);
      }
    } catch (error) {
      // 404 is expected if no completion exists
      if (error.response?.status !== 404) {
        console.error('Error fetching brand voice completion:', error);
      }
      setBrandVoiceCompletion(null);
    }
  };

  const fetchPendingBrandVoiceCompletions = async (brand) => {
    const brandId = brand._id || brand.id;

    try {
      const response = await axios.get(`/api/brand-voice/pending/${brandId}`, {
        withCredentials: true
      });
      setPendingBrandVoiceCompletions(response.data.pending || []);
    } catch (error) {
      console.error('Error fetching pending brand voice completions:', error);
      setPendingBrandVoiceCompletions([]);
    }
  };

  const fetchBrandVoiceHistory = async (brand) => {
    if (!brand) return;
    const brandId = brand._id || brand.id;

    setHistoryLoading(true);
    try {
      const response = await axios.get(`/api/brand-voice/history/${brandId}`, {
        withCredentials: true
      });
      setBrandVoiceHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching brand voice history:', error);
      setBrandVoiceHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const fetchBrandHubActivity = async (brand) => {
    if (!brand) return;
    const brandId = brand._id || brand.id;

    setActivityLoading(true);
    try {
      // Pull the full activity log up front (looping over server pages) so it can be
      // paginated client-side instead of growing an ever-taller scrolling list.
      let allActivity = [];
      let offset = 0;
      let hasMore = true;
      while (hasMore) {
        const response = await axios.get(`/api/brand-hub/activity/${brandId}`, {
          params: { limit: ACTIVITY_FETCH_BATCH_SIZE, offset },
          withCredentials: true
        });
        const batch = response.data.activity || [];
        allActivity = allActivity.concat(batch);
        hasMore = !!response.data.has_more && batch.length > 0;
        offset += batch.length;
      }
      setBrandHubActivity(allActivity);
      setActivityPage(1);
    } catch (error) {
      console.error('Error fetching brand hub activity:', error);
      setBrandHubActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const formatHistoryValue = (value) => {
    if (value === null || value === undefined || value === '') return '(empty)';
    if (Array.isArray(value)) {
      if (value.length === 0) return '(empty)';
      return value.map(v => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n');
    }
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Line-level LCS diff so only the lines that actually changed are called out,
  // instead of dumping the whole before/after block when one line was edited.
  const computeLineDiff = (oldText, newText) => {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const m = oldLines.length;
    const n = newLines.length;
    const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
      for (let j = n - 1; j >= 0; j--) {
        dp[i][j] = oldLines[i] === newLines[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
    const diff = [];
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
      if (oldLines[i] === newLines[j]) {
        diff.push({ type: 'equal', text: oldLines[i] });
        i++;
        j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        diff.push({ type: 'removed', text: oldLines[i] });
        i++;
      } else {
        diff.push({ type: 'added', text: newLines[j] });
        j++;
      }
    }
    while (i < m) {
      diff.push({ type: 'removed', text: oldLines[i] });
      i++;
    }
    while (j < n) {
      diff.push({ type: 'added', text: newLines[j] });
      j++;
    }
    return diff;
  };

  const handleActivityEntryClick = (entry) => {
    const lineDiff = computeLineDiff(formatHistoryValue(entry.old_value), formatHistoryValue(entry.new_value));

    modal.custom({
      title: entry.field || 'Change Details',
      size: 'medium',
      content: (
        <div className="history-diff-view">
          {lineDiff.map((line, index) => (
            <div key={index} className={`history-diff-line history-diff-line-${line.type}`}>
              <span className="history-diff-marker">
                {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ''}
              </span>
              <span className="history-diff-text">{line.text || ' '}</span>
            </div>
          ))}
        </div>
      )
    });
  };

  const handleDeleteBrandVoiceHistoryEntry = async (item) => {
    const confirmed = await modal.confirm(
      `Delete this Brand Voice submission from ${item.client_name || 'this client'}? This cannot be undone.`,
      'Delete History Item',
      { confirmText: 'Delete', cancelText: 'Cancel', size: 'small' }
    );
    if (!confirmed) return;

    try {
      await axios.delete(`/api/brand-voice/history/entry/${item._id}`, { withCredentials: true });
      setBrandVoiceHistory(prev => prev.filter(h => h._id !== item._id));
      setExpandedHistoryId(prev => (prev === item._id ? null : prev));
      modal.toast('History item deleted', 'success');
    } catch (error) {
      console.error('Error deleting brand voice history entry:', error);
      modal.error('Failed to delete history item');
    }
  };

  const handleDeleteActivityEntry = async (entry) => {
    const confirmed = await modal.confirm(
      `Delete the activity log entry for "${entry.field || 'this field'}"? This cannot be undone.`,
      'Delete History Item',
      { confirmText: 'Delete', cancelText: 'Cancel', size: 'small' }
    );
    if (!confirmed) return;

    try {
      await axios.delete(`/api/brand-hub/activity/entry/${entry._id}`, { withCredentials: true });
      setBrandHubActivity(prev => {
        const updated = prev.filter(item => item._id !== entry._id);
        const newTotalPages = Math.max(1, Math.ceil(updated.length / ACTIVITY_ITEMS_PER_PAGE));
        setActivityPage(page => Math.min(page, newTotalPages));
        return updated;
      });
      modal.toast('History item deleted', 'success');
    } catch (error) {
      console.error('Error deleting brand hub activity entry:', error);
      modal.error('Failed to delete history item');
    }
  };

  const activityTotalPages = Math.max(1, Math.ceil(brandHubActivity.length / ACTIVITY_ITEMS_PER_PAGE));
  const paginatedActivity = brandHubActivity.slice(
    (activityPage - 1) * ACTIVITY_ITEMS_PER_PAGE,
    activityPage * ACTIVITY_ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (activeTab === 'history' && canViewHistory && selectedBrand) {
      fetchBrandVoiceHistory(selectedBrand);
      fetchBrandHubActivity(selectedBrand);
    }
  }, [activeTab, selectedBrand]);

  const handleApproveBrandVoice = async (completion) => {
    if (!selectedBrand || !completion) return;

    const result = await modal.confirm(`Are you sure you want to approve ${completion.client_name || "this client's"} brand voice? This will save it to the brand profile and replace any currently active brand voice.`, 'Approve Brand Voice');
    if (!result) {
      return;
    }

    setApproving(true);
    setError(null);
    setSuccess(null);

    try {
      const brandId = selectedBrand._id || selectedBrand.id;
      await axios.post(`/api/brand-voice/approve`, {
        brand_id: brandId,
        completion_id: completion._id
      }, { withCredentials: true });

      // Fetch updated brand data
      const response = await axios.get(`/api/brands/${brandId}`);
      const updatedBrand = response.data;

      setSelectedBrand(updatedBrand);

      // Update brand voice UI with the new data
      setBrandVoice({
        persona: updatedBrand.brand_voice?.persona || '',
        traits: updatedBrand.brand_voice?.traits || '',
        guidelines: updatedBrand.brand_voice?.guidelines || '',
        excerpts: updatedBrand.brand_voice?.excerpts || [],
        samples: updatedBrand.brand_voice?.samples || []
      });

      // Update local state directly instead of re-fetching the pending/history lists -
      // avoids a visible loading flash on the History panel for what's otherwise an
      // instant change.
      const nowIso = new Date().toISOString();
      const approverName = user?.name || user?.email || 'You';

      setPendingBrandVoiceCompletions(prev => prev.filter(c => c._id !== completion._id));

      setBrandVoiceHistory(prev => prev.map(item => {
        if (item._id === completion._id) {
          return { ...item, status: 'approved', is_active: true, approved_at: nowIso, approved_by_name: approverName, rejected_at: null, rejected_by_name: null, superseded_at: null };
        }
        if (item.is_active) {
          return { ...item, is_active: false, superseded_at: nowIso };
        }
        return item;
      }));

      setBrandVoiceCompletion(prev => ({
        ...(prev || {}),
        _id: completion._id,
        client_name: completion.client_name,
        status: 'approved',
        approved_at: nowIso,
        final_winner: completion.final_winner,
        persona_data: completion.persona_data
      }));

      // Show success modal
      modal.success('Brand voice approved successfully!');
    } catch (error) {
      console.error('Error approving brand voice:', error);
      modal.error(error.response?.data?.error || 'Failed to approve brand voice');
    } finally {
      setApproving(false);
    }
  };

  const handleRejectBrandVoice = async (completion) => {
    if (!selectedBrand || !completion) return;

    const result = await modal.confirm(`Are you sure you want to reject ${completion.client_name || "this client's"} brand voice? The client will be able to create a new submission.`, 'Reject Brand Voice');
    if (!result) {
      return;
    }

    setApproving(true);

    try {
      const brandId = selectedBrand._id || selectedBrand.id;
      await axios.post(`/api/brand-voice/reject`, {
        brand_id: brandId,
        completion_id: completion._id
      }, { withCredentials: true });

      // Update local state directly instead of re-fetching the pending/history lists -
      // avoids a visible loading flash on the History panel for what's otherwise an
      // instant change.
      const nowIso = new Date().toISOString();
      const rejecterName = user?.name || user?.email || 'You';

      setPendingBrandVoiceCompletions(prev => prev.filter(c => c._id !== completion._id));

      setBrandVoiceHistory(prev => prev.map(item =>
        item._id === completion._id
          ? { ...item, status: 'rejected', rejected_at: nowIso, rejected_by_name: rejecterName }
          : item
      ));

      // The description-label completion is a small, separate fetch with no loading
      // state of its own, so refreshing it here doesn't cause any visible flash.
      await fetchBrandVoiceCompletion(selectedBrand);

      // Show success modal
      modal.success('Brand voice rejected. The client can now submit a new version.');
    } catch (error) {
      console.error('Error rejecting brand voice:', error);
      modal.error(error.response?.data?.error || 'Failed to reject brand voice');
    } finally {
      setApproving(false);
    }
  };

  const handleDocumentChange = (field, value) => {
    setBrandDocument(prev => ({ ...prev, [field]: value }));
  };

  const handleVoiceChange = (field, value) => {
    setBrandVoice(prev => ({ ...prev, [field]: value }));
  };

  const addArrayItem = (formType, field) => {
    const key = `${formType}_${field}`;
    const value = newItem[key]?.trim();
    if (!value) return;

    if (formType === 'document') {
      setBrandDocument(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value]
      }));
    } else {
      setBrandVoice(prev => ({
        ...prev,
        [field]: [...(prev[field] || []), value]
      }));
    }
    setNewItem(prev => ({ ...prev, [key]: '' }));
  };

  const removeArrayItem = (formType, field, index) => {
    if (formType === 'document') {
      setBrandDocument(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    } else {
      setBrandVoice(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  const handleSave = async () => {
    if (!selectedBrand) return;

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const brandId = selectedBrand._id || selectedBrand.id;

      // Save brand document
      try {
        await axios.put(`/api/brands/${brandId}/document`, {
          brand_document: brandDocument
        }, { withCredentials: true });
      } catch (err) {
        throw new Error(`Failed to save brand document: ${err.response?.data?.error || err.message}`);
      }

      // Save brand voice
      try {
        await axios.put(`/api/brands/${brandId}/voice`, {
          brand_voice: brandVoice
        }, { withCredentials: true });
      } catch (err) {
        throw new Error(`Failed to save brand voice: ${err.response?.data?.error || err.message}`);
      }

      // Save brand visuals (colors)
      try {
        console.log('Sending brand visuals update:', brandVisuals);
        await axios.put(`/api/brands/${brandId}/visuals`, brandVisuals, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
      } catch (err) {
        console.error('Brand visuals save error:', err);
        throw new Error(`Failed to save brand visuals: ${err.response?.data?.error || err.message}`);
      }

      // Fetch updated brand data to reflect changes in context
      const response = await axios.get(`/api/brands/${brandId}`, {
        withCredentials: true
      });
      const updatedBrand = response.data;

      // Update local state
      setSelectedBrand(updatedBrand);

      // Update form fields with the saved data
      setBrandDocument({
        audience: updatedBrand.brand_document?.audience || '',
        mission_core_values: updatedBrand.brand_document?.mission_core_values || '',
        value_propositions: updatedBrand.brand_document?.value_propositions || [],
        differentiators: updatedBrand.brand_document?.differentiators || [],
        products_services: updatedBrand.brand_document?.products_services || '',
        locations: updatedBrand.brand_document?.locations || [],
        ideal_clients: updatedBrand.brand_document?.ideal_clients || '',
        industry_details: updatedBrand.brand_document?.industry_details || '',
        competitors: updatedBrand.brand_document?.competitors || [],
        offers: updatedBrand.brand_document?.offers || []
      });

      setBrandVoice({
        persona: updatedBrand.brand_voice?.persona || '',
        traits: updatedBrand.brand_voice?.traits || '',
        guidelines: updatedBrand.brand_voice?.guidelines || '',
        excerpts: updatedBrand.brand_voice?.excerpts || [],
        samples: updatedBrand.brand_voice?.samples || []
      });

      setBrandVisuals({
        menu_color: updatedBrand.menu_color || '#2E3336',
        primary_color: updatedBrand.primary_color || '#299DDB',
        secondary_color: updatedBrand.secondary_color || '#20E27A',
        tertiary_color: updatedBrand.tertiary_color || '#FF5524',
        light_color: updatedBrand.light_color || '#FFFFFF',
        dark_color: updatedBrand.dark_color || '#2B2D2F'
      });

      // Update context so Layout.js gets the new menu_color
      if (contextBrand && (contextBrand._id === brandId || contextBrand.id === brandId)) {
        // Trigger a refresh of the context
        window.dispatchEvent(new CustomEvent('brand-updated', { detail: updatedBrand }));
      }

      setSuccess('Brand assets saved successfully!');
    } catch (err) {
      console.error('Error saving brand assets:', err);
      setError(err.message || err.response?.data?.error || 'An unexpected error occurred');
    } finally {
      setSaving(false);
    }
  };

  const renderArrayField = (formType, field, label, placeholder) => {
    const items = formType === 'document' ? brandDocument[field] : brandVoice[field];
    const key = `${formType}_${field}`;

    return (
      <div className="form-group array-field">
        <label>{label}</label>
        <div className="array-items">
          {items?.map((item, index) => (
            <span key={index} className="array-tag">
              {item}
              <button
                type="button"
                className="remove-tag"
                onClick={() => removeArrayItem(formType, field, index)}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="array-input">
          <input
            type="text"
            value={newItem[key] || ''}
            onChange={(e) => setNewItem(prev => ({ ...prev, [key]: e.target.value }))}
            placeholder={placeholder}
            autoComplete="off"
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            data-form-type="other"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addArrayItem(formType, field);
              }
            }}
          />
          <button
            type="button"
            className="add-btn"
            onClick={() => addArrayItem(formType, field)}
          >
            Add
          </button>
        </div>
      </div>
    );
  };

  const renderLongArrayField = (formType, field, label, placeholder) => {
    const items = formType === 'document' ? brandDocument[field] : brandVoice[field];
    const key = `${formType}_${field}`;

    return (
      <div className="form-group long-array-field">
        <div className="long-array-items">
          {items?.map((item, index) => (
            <div key={index} className="long-array-item">
              <div className="long-array-item-header">
                <span className="item-label">{label} {index + 1}</span>
                <button
                  type="button"
                  className="remove-item-btn"
                  onClick={() => removeArrayItem(formType, field, index)}
                >
                  Remove
                </button>
              </div>
              <div className="long-array-item-content">
                {item}
              </div>
            </div>
          ))}
        </div>
        <div className="long-array-input">
          <textarea
            value={newItem[key] || ''}
            onChange={(e) => setNewItem(prev => ({ ...prev, [key]: e.target.value }))}
            placeholder={placeholder}
            rows={5}
          />
          <button
            type="button"
            className="add-btn"
            onClick={() => addArrayItem(formType, field)}
          >
            + Add {label}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="super-admin-internal-layout">
      {!selectedBrand ? (
        <div className="no-selection">
          <h3>No Brand Selected</h3>
          <p>Please select a brand from the context menu at the top to manage its profile, voice, and visual identity.</p>
        </div>
      ) : (
        <>
          <nav className="internal-nav">
            <div className="internal-nav-items">
              <button
                className={`internal-nav-btn ${activeTab === 'document' ? 'active' : ''}`}
                onClick={() => setActiveTab('document')}
              >
                Profile
              </button>
              <button
                className={`internal-nav-btn ${activeTab === 'voice' ? 'active' : ''}`}
                onClick={() => setActiveTab('voice')}
              >
                Voice
              </button>
              <button
                className={`internal-nav-btn ${activeTab === 'visuals' ? 'active' : ''}`}
                onClick={() => setActiveTab('visuals')}
              >
                Visuals
              </button>
              {canViewHistory && (
                <button
                  className={`internal-nav-btn ${activeTab === 'history' ? 'active' : ''}`}
                  onClick={() => setActiveTab('history')}
                >
                  History
                </button>
              )}
            </div>
          </nav>

          <div className="internal-content">
            {error && (
              <div className="alert alert-error">
                {error}
                <button onClick={() => setError(null)}>&times;</button>
              </div>
            )}

            {success && (
              <div className="alert alert-success">
                {success}
                <button onClick={() => setSuccess(null)}>&times;</button>
              </div>
            )}

            {activeTab === 'document' ? (
                <div className="editor-form">
                  <div className="form-section">
                    <h4>Brand Profile</h4>
                    <p className="section-description">
                      This information is used by AI to understand your brand when generating content ideas.
                    </p>

                    <div className="form-group">
                      <label>Audience</label>
                      <textarea
                        value={brandDocument.audience}
                        onChange={(e) => handleDocumentChange('audience', e.target.value)}
                        placeholder="Who is your target audience? Demographics, interests, behaviors..."
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>Mission & Core Values</label>
                      <textarea
                        value={brandDocument.mission_core_values}
                        onChange={(e) => handleDocumentChange('mission_core_values', e.target.value)}
                        placeholder="What is the brand's mission? What core values drive the business?"
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>Value Propositions</label>
                      {renderLongArrayField('document', 'value_propositions', 'Value Proposition', 'Add a value proposition...')}
                    </div>

                    <div className="form-group">
                      <label>Differentiators</label>
                      {renderLongArrayField('document', 'differentiators', 'Differentiator', 'Add a differentiator...')}
                    </div>

                    <div className="form-group">
                      <label>Products & Services</label>
                      <textarea
                        value={brandDocument.products_services}
                        onChange={(e) => handleDocumentChange('products_services', e.target.value)}
                        placeholder="What products or services does this brand offer?"
                        rows={3}
                      />
                    </div>

                    {renderArrayField('document', 'locations', 'Locations', 'Add a location...')}

                    <div className="form-group">
                      <label>Ideal Clients</label>
                      <textarea
                        value={brandDocument.ideal_clients}
                        onChange={(e) => handleDocumentChange('ideal_clients', e.target.value)}
                        placeholder="Describe the ideal client profile for this brand..."
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>Industry Details</label>
                      <textarea
                        value={brandDocument.industry_details}
                        onChange={(e) => handleDocumentChange('industry_details', e.target.value)}
                        placeholder="What industry is this brand in? Any specific niche or sector details..."
                        rows={2}
                      />
                    </div>

                    {renderArrayField('document', 'competitors', 'Competitors', 'Add a competitor...')}

                    <div className="form-group">
                      <label>Offers</label>
                      {renderLongArrayField('document', 'offers', 'Offer', 'Add a current offer or promotion...')}
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      className="save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : activeTab === 'voice' ? (
                <div className="editor-form">
                  {pendingBrandVoiceCompletions.map((completion) => (
                    <div className="brand-voice-approval-alert" key={completion._id}>
                      <div className="approval-alert-header">
                        <strong>Brand Voice Awaiting Approval</strong>
                      </div>
                      <p className="approval-alert-text">
                        {completion.client_name || 'A client'} completed the Brand Voice Designer on{' '}
                        {new Date(completion.completed_at).toLocaleDateString()}.
                        Review the generated voice below and approve to save it to the brand profile.
                      </p>
                      <div className="approval-buttons-container">
                        <button
                          className="approve-brand-voice-btn"
                          onClick={() => handleApproveBrandVoice(completion)}
                          disabled={approving}
                        >
                          {approving ? 'Processing...' : 'Approve Brand Voice'}
                        </button>
                        <button
                          className="reject-brand-voice-btn"
                          onClick={() => handleRejectBrandVoice(completion)}
                          disabled={approving}
                        >
                          {approving ? 'Processing...' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  ))}


                  {/* Brand Voice Preview - Hidden per user request */}
                  {/* {brandVoiceCompletion && brandVoiceCompletion.status === 'completed' && brandVoiceCompletion.persona_data && (
                    <div className="brand-voice-preview" style={{
                      background: '#f8f9fa',
                      border: '1px solid #dee2e6',
                      borderRadius: '8px',
                      padding: '20px',
                      marginBottom: '24px'
                    }}>
                      <h4 style={{marginTop: 0}}>Generated Brand Voice ({brandVoiceCompletion.final_winner || 'Voice'})</h4>

                      {brandVoiceCompletion.persona_data.introduction && (
                        <div style={{marginBottom: '20px'}}>
                          <strong>Persona:</strong>
                          <p style={{marginTop: '8px', whiteSpace: 'pre-wrap'}}>{brandVoiceCompletion.persona_data.introduction}</p>
                        </div>
                      )}

                      {brandVoiceCompletion.persona_data.traits && brandVoiceCompletion.persona_data.traits.length > 0 && (
                        <div style={{marginBottom: '20px'}}>
                          <strong>Traits:</strong>
                          <ul style={{marginTop: '8px', paddingLeft: '20px'}}>
                            {brandVoiceCompletion.persona_data.traits.map((trait, idx) => (
                              <li key={idx}>
                                <strong>{trait.name}:</strong> {trait.description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {brandVoiceCompletion.persona_data.guidelines && brandVoiceCompletion.persona_data.guidelines.length > 0 && (
                        <div>
                          <strong>Guidelines:</strong>
                          <ul style={{marginTop: '8px', paddingLeft: '20px'}}>
                            {brandVoiceCompletion.persona_data.guidelines.map((guideline, idx) => (
                              <li key={idx}>{guideline}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )} */}

                  <div className="form-section">
                    <div className="form-group">
                      <label>Persona</label>
                      <textarea
                        value={brandVoice.persona}
                        onChange={(e) => handleVoiceChange('persona', e.target.value)}
                        placeholder="Describe the brand's persona. If the brand were a person, who would they be? What's their background, attitude, and way of speaking?"
                        rows={2}
                      />
                    </div>

                    <div className="form-group">
                      <label>Traits</label>
                      <textarea
                        value={brandVoice.traits}
                        onChange={(e) => handleVoiceChange('traits', e.target.value)}
                        placeholder="List the key personality traits of this brand voice (e.g., confident, friendly, witty, professional, approachable)..."
                        rows={4}
                      />
                    </div>

                    <div className="form-group">
                      <label>Guidelines</label>
                      <textarea
                        value={brandVoice.guidelines}
                        onChange={(e) => handleVoiceChange('guidelines', e.target.value)}
                        placeholder="Specific guidelines for writing in this brand's voice. What to do, what to avoid, tone considerations..."
                        rows={6}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Excerpts</label>
                    <p className="section-description">
                      1-2 sentence examples of the brand voice in action.
                    </p>
                    {renderLongArrayField('voice', 'excerpts', 'Excerpt', 'Add a short excerpt (1-2 sentences)...')}
                  </div>

                  <div className="form-group">
                    <label>Samples</label>
                    <p className="section-description">
                      Full-length examples of the brand voice in action.
                    </p>
                    {renderLongArrayField('voice', 'samples', 'Sample', 'Write a full-length sample of brand voice content...')}
                  </div>

                  <div className="form-actions">
                    <button
                      className="save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : activeTab === 'visuals' ? (
                <div className="editor-form">
                  <p className="visuals-description">
                    Manage your brand's visual identity including logos and brand colors.
                  </p>

                  {/* Brand Colors */}
                  <div className="form-section">
                    <h4>Brand Colors</h4>

                    <div className="color-section-wrapper">
                      <div className="color-cards-layout">
                        {/* Primary Color - Large Card */}
                        <div
                          className="color-card-large"
                          style={{ backgroundColor: brandVisuals.primary_color }}
                          onClick={() => document.getElementById('primary-color-input').click()}
                        >
                          <div className="color-card-footer">
                            <span className="color-label">Primary Color</span>
                            <span className="color-hex">{brandVisuals.primary_color}</span>
                          </div>
                        </div>

                      {/* Small Color Cards */}
                        <div className="color-cards-small">
                          {/* Secondary Color */}
                          <div
                            className="color-card-small"
                            style={{ backgroundColor: brandVisuals.secondary_color }}
                            onClick={() => document.getElementById('secondary-color-input').click()}
                          >
                            <div className="color-card-footer-small">
                              <span className="color-label-small">Secondary Color</span>
                              <span className="color-hex-small">{brandVisuals.secondary_color}</span>
                            </div>
                          </div>

                          {/* Tertiary Color */}
                          <div
                            className="color-card-small"
                            style={{ backgroundColor: brandVisuals.tertiary_color }}
                            onClick={() => document.getElementById('tertiary-color-input').click()}
                          >
                            <div className="color-card-footer-small">
                              <span className="color-label-small">Tertiary Color</span>
                              <span className="color-hex-small">{brandVisuals.tertiary_color}</span>
                            </div>
                          </div>

                          {/* Light Color */}
                          <div
                            className="color-card-small"
                            style={{ backgroundColor: brandVisuals.light_color }}
                            onClick={() => document.getElementById('light-color-input').click()}
                          >
                            <div className="color-card-footer-small">
                              <span className="color-label-small">Light Color</span>
                              <span className="color-hex-small">{brandVisuals.light_color}</span>
                            </div>
                          </div>

                          {/* Dark Color */}
                          <div
                            className="color-card-small"
                            style={{ backgroundColor: brandVisuals.dark_color }}
                            onClick={() => document.getElementById('dark-color-input').click()}
                          >
                            <div className="color-card-footer-small">
                              <span className="color-label-small">Dark Color</span>
                              <span className="color-hex-small">{brandVisuals.dark_color}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Hidden Color Inputs */}
                      <div className="color-inputs-container">
                        <input
                          id="primary-color-input"
                          type="color"
                          value={brandVisuals.primary_color}
                          onChange={(e) => setBrandVisuals({ ...brandVisuals, primary_color: e.target.value })}
                          className="hidden-color-input"
                        />
                        <input
                          id="menu-color-input"
                          type="color"
                          value={brandVisuals.menu_color}
                          onChange={(e) => setBrandVisuals({ ...brandVisuals, menu_color: e.target.value })}
                          className="hidden-color-input"
                        />
                        <input
                          id="secondary-color-input"
                          type="color"
                          value={brandVisuals.secondary_color}
                          onChange={(e) => setBrandVisuals({ ...brandVisuals, secondary_color: e.target.value })}
                          className="hidden-color-input"
                        />
                        <input
                          id="tertiary-color-input"
                          type="color"
                          value={brandVisuals.tertiary_color}
                          onChange={(e) => setBrandVisuals({ ...brandVisuals, tertiary_color: e.target.value })}
                          className="hidden-color-input"
                        />
                        <input
                          id="light-color-input"
                          type="color"
                          value={brandVisuals.light_color}
                          onChange={(e) => setBrandVisuals({ ...brandVisuals, light_color: e.target.value })}
                          className="hidden-color-input"
                        />
                        <input
                          id="dark-color-input"
                          type="color"
                          value={brandVisuals.dark_color}
                          onChange={(e) => setBrandVisuals({ ...brandVisuals, dark_color: e.target.value })}
                          className="hidden-color-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* UI Elements */}
                  <div className="form-section">
                    <h4>UI Elements</h4>

                    <div className="ui-element-preview" style={{ '--menu-color': brandVisuals.menu_color }}>
                      <label>Brand Bar Selector</label>

                      <div
                        className="brand-bar-preview clickable"
                        onClick={() => document.getElementById('menu-color-input').click()}
                      >
                        <div className="brand-bar-color-info">
                          <span className="color-label-small">Brand Bar</span>
                          <span className="color-hex-small">{brandVisuals.menu_color}</span>
                        </div>
                        <div className="brand-context-card-preview">
                          <div className="brand-logo-preview">
                            {selectedBrand.icon_url ? (
                              <BrandIcon src={selectedBrand.icon_url} alt="Brand logo" />
                            ) : (
                              <div className="brand-logo-placeholder">
                                {(selectedBrand.brand_name || selectedBrand.name || 'B').charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="brand-name-preview">{selectedBrand.brand_name || selectedBrand.name}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Logos */}
                  <div className="form-section">
                    <h4>Logos</h4>

                    <div className="logo-preview">
                      <label>Favicon</label>
                      <div className="favicon-display">
                        {selectedBrand.icon_url ? (
                          <BrandIcon src={selectedBrand.icon_url} alt="Brand favicon" className="favicon-image" />
                        ) : (
                          <div className="favicon-placeholder">
                            {(selectedBrand.brand_name || selectedBrand.name || 'B').charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="form-actions">
                    <button
                      className="save-btn"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : canViewHistory ? (
                <div className="editor-form">
                  <div className="form-section">
                    <h4>Brand Voice History</h4>
                    <p className="section-description">
                      Every Brand Voice Designer submission for this brand, and its outcome.
                    </p>

                    {historyLoading && brandVoiceHistory.length === 0 ? (
                      <p className="history-loading-text">Loading history...</p>
                    ) : brandVoiceHistory.length === 0 ? (
                      <p className="history-empty-text">No brand voice submissions yet for this brand.</p>
                    ) : (
                      <div className="brand-voice-history-list">
                        {brandVoiceHistory.map((item) => {
                          const isExpanded = expandedHistoryId === item._id;
                          const statusLabel = item.status === 'approved'
                            ? (item.is_active ? 'Approved' : 'Superseded')
                            : item.status === 'rejected'
                              ? 'Rejected'
                              : 'Pending Review';
                          const statusClass = item.status === 'approved'
                            ? (item.is_active ? 'approved' : 'superseded')
                            : item.status === 'rejected'
                              ? 'rejected'
                              : 'pending';

                          return (
                            <div
                              key={item._id}
                              className="history-item history-item-expandable"
                              onClick={() => setExpandedHistoryId(isExpanded ? null : item._id)}
                              onKeyDown={(e) => { if (e.key === 'Enter') setExpandedHistoryId(isExpanded ? null : item._id); }}
                              role="button"
                              tabIndex={0}
                            >
                              <div className="history-item-header">
                                <div className="history-item-main">
                                  <span className={`history-status-badge history-status-${statusClass}`}>
                                    {statusLabel}
                                  </span>
                                  <span className="history-client-name">{item.client_name}</span>
                                  <span className="history-submitted-date">
                                    {/* submitted {item.completed_at ? new Date(item.completed_at).toLocaleString() : 'Unknown'} */}
                                    submitted {item.completed_at ? new Date(item.completed_at).toLocaleDateString() : 'Unknown'}
                                  </span>
                                </div>
                                <div className="history-item-actions">
                                  {/* <button
                                    className="history-comments-btn"
                                    onClick={() => setCommentModalState({
                                      open: true,
                                      itemId: item._id,
                                      comments: item.comments || []
                                    })}
                                  >
                                    Comments {item.comments?.length ? `(${item.comments.length})` : ''}
                                  </button> */}
                                  {!(item.status === 'approved' && item.is_active) && (
                                    <button
                                      className="history-set-approved-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleApproveBrandVoice(item);
                                      }}
                                      disabled={approving}
                                      title="Make this submission the active, approved brand voice"
                                    >
                                      {approving ? 'Processing...' : 'Set as Approved'}
                                    </button>
                                  )}
                                  {canDeleteActivity && (
                                    <button
                                      type="button"
                                      className="history-item-delete-btn"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteBrandVoiceHistoryEntry(item);
                                      }}
                                      title="Delete this history item"
                                      aria-label="Delete this history item"
                                    >
                                      <Icon name="close-x" size="0.875rem" />
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Only the current status's own decision shows here - not every
                                  past action taken on this submission (e.g. rejected, then later
                                  approved, then superseded). The "Superseded" badge above already
                                  conveys that this is no longer the active approved voice. */}
                              {item.status === 'approved' && item.approved_at && (
                                <p className="history-decision-line">
                                  Approved by {item.approved_by_name || 'Unknown'} on {new Date(item.approved_at).toLocaleDateString()}
                                </p>
                              )}
                              {item.status === 'rejected' && item.rejected_at && (
                                <p className="history-decision-line">
                                  Rejected by {item.rejected_by_name || 'Unknown'} on {new Date(item.rejected_at).toLocaleDateString()}
                                </p>
                              )}

                              {isExpanded && (
                                <div className="history-item-details">
                                  {item.persona_data?.introduction && (
                                    <div className="history-detail-block">
                                      <strong>Persona:</strong>
                                      <p>{item.persona_data.introduction}</p>
                                    </div>
                                  )}

                                  {item.persona_data?.traits && item.persona_data.traits.length > 0 && (
                                    <div className="history-detail-block">
                                      <strong>Traits:</strong>
                                      <ul>
                                        {item.persona_data.traits.map((trait, idx) => (
                                          <li key={idx}>
                                            <strong>{trait.name}:</strong> {trait.description}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}

                                  {item.persona_data?.guidelines && item.persona_data.guidelines.length > 0 && (
                                    <div className="history-detail-block">
                                      <strong>Guidelines:</strong>
                                      <ul>
                                        {item.persona_data.guidelines.map((guideline, idx) => (
                                          <li key={idx}>{guideline}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="form-section">
                    <h4>Brand Hub Activity</h4>
                    <p className="section-description">
                      Every edit made to this brand's Profile, Voice, and Visuals.
                    </p>

                    {activityLoading && brandHubActivity.length === 0 ? (
                      <p className="history-loading-text">Loading activity...</p>
                    ) : brandHubActivity.length === 0 ? (
                      <p className="history-empty-text">No Brand Hub edits recorded yet for this brand.</p>
                    ) : (
                      <div className="brand-hub-activity-list">
                        <div className="brand-hub-activity-scroll">
                          {paginatedActivity.map((entry) => (
                            <div
                              key={entry._id}
                              className="history-item history-item-clickable"
                              onClick={() => handleActivityEntryClick(entry)}
                              onKeyDown={(e) => { if (e.key === 'Enter') handleActivityEntryClick(entry); }}
                              role="button"
                              tabIndex={0}
                            >
                              <p className="history-decision-line">
                                <strong>{entry.field || 'A field'}</strong> was modified by {entry.performed_by_name || 'Unknown'} on {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : 'Unknown'}
                              </p>
                              {canDeleteActivity && (
                                <button
                                  type="button"
                                  className="history-item-delete-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteActivityEntry(entry);
                                  }}
                                  title="Delete this history item"
                                  aria-label="Delete this history item"
                                >
                                  <Icon name="close-x" size="0.875rem" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        {activityTotalPages > 1 && (
                          <div className="activity-pagination-controls">
                            <button
                              type="button"
                              className="activity-pagination-btn"
                              onClick={() => setActivityPage(prev => Math.max(1, prev - 1))}
                              disabled={activityPage === 1}
                            >
                              <Icon name="left-open" size="1rem" />
                              Previous
                            </button>
                            <div className="activity-pagination-numbers">
                              {Array.from({ length: Math.min(5, activityTotalPages) }, (_, i) => {
                                let pageNum;
                                if (activityTotalPages <= 5) {
                                  pageNum = i + 1;
                                } else if (activityPage <= 3) {
                                  pageNum = i + 1;
                                } else if (activityPage >= activityTotalPages - 2) {
                                  pageNum = activityTotalPages - 4 + i;
                                } else {
                                  pageNum = activityPage - 2 + i;
                                }
                                return (
                                  <button
                                    type="button"
                                    key={pageNum}
                                    className={`activity-pagination-number ${activityPage === pageNum ? 'active' : ''}`}
                                    onClick={() => setActivityPage(pageNum)}
                                  >
                                    {pageNum}
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              type="button"
                              className="activity-pagination-btn"
                              onClick={() => setActivityPage(prev => Math.min(activityTotalPages, prev + 1))}
                              disabled={activityPage === activityTotalPages}
                            >
                              Next
                              <Icon name="right-open" size="1rem" />
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>

      <CommentModal
        isOpen={commentModalState.open}
        onClose={() => setCommentModalState({ open: false, itemId: null, comments: [] })}
        itemId={commentModalState.itemId}
        itemType="brand-voice-completion"
        comments={commentModalState.comments}
        user={user}
        onCommentAdded={(updatedComments) => {
          setBrandVoiceHistory(prev => prev.map(item =>
            item._id === commentModalState.itemId
              ? { ...item, comments: updatedComments }
              : item
          ));
        }}
        onCommentDeleted={(updatedComments) => {
          setBrandVoiceHistory(prev => prev.map(item =>
            item._id === commentModalState.itemId
              ? { ...item, comments: updatedComments }
              : item
          ));
        }}
      />
    </>
    );
  };

export default BrandAssets;
